import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import {
  UserProfile,
  TenantInfo,
  normalizeRole,
  resolveEffectivePermissions,
} from '../lib/permissions'

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: UserProfile | null
  permissions: Set<string>
  modules: string[]
  tenant: TenantInfo | null
  isSuperAdmin: boolean
  canCreateSuperAdmins: boolean
  loading: boolean
  permissionLoading: boolean
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/** Survives PWA/camera full reloads when the network is down. */
const PERMS_CACHE_KEY = 'rbcfs.auth.permissions.v1'

type CachedAuthPayload = {
  userId: string
  profile: UserProfile
  permissions: string[]
  modules: string[]
  tenant: TenantInfo | null
  isSuperAdmin: boolean
  canCreateSuperAdmins: boolean
}

function readPermissionCache(userId: string): CachedAuthPayload | null {
  try {
    const raw = localStorage.getItem(PERMS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedAuthPayload
    if (!parsed?.userId || parsed.userId !== userId || !parsed.profile) return null
    return parsed
  } catch {
    return null
  }
}

function writePermissionCache(payload: CachedAuthPayload) {
  try {
    localStorage.setItem(PERMS_CACHE_KEY, JSON.stringify(payload))
  } catch {
    /* private mode / quota */
  }
}

function clearPermissionCache() {
  try {
    localStorage.removeItem(PERMS_CACHE_KEY)
  } catch {
    /* ignore */
  }
}

function applyCachedAuth(
  cached: CachedAuthPayload,
  setters: {
    setProfile: (p: UserProfile | null) => void
    setPermissions: (p: Set<string>) => void
    setModules: (m: string[]) => void
    setTenant: (t: TenantInfo | null) => void
    setIsSuperAdmin: (v: boolean) => void
    setCanCreateSuperAdmins: (v: boolean) => void
  }
) {
  setters.setProfile(cached.profile)
  setters.setPermissions(new Set(cached.permissions))
  setters.setModules(cached.modules || [])
  setters.setTenant(cached.tenant)
  setters.setIsSuperAdmin(!!cached.isSuperAdmin)
  setters.setCanCreateSuperAdmins(!!cached.canCreateSuperAdmins)
}

async function fetchViaApi(accessToken: string) {
  const res = await fetch('/api/admin/me/permissions', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (!res.ok) return null
  const data = await res.json()
  if (!data.profile) return null
  const role = normalizeRole(data.profile.role)
  return {
    profile: { ...data.profile, role } as UserProfile,
    permissions: new Set<string>(data.permissions || []),
    modules: (data.modules || []) as string[],
    tenant: (data.tenant || null) as TenantInfo | null,
    isSuperAdmin: !!data.isSuperAdmin,
    canCreateSuperAdmins: !!(data.profile?.is_super_admin && data.profile?.can_create_super_admins),
  }
}

async function fetchProfileAndPermissions(user: User, accessToken?: string) {
  if (accessToken) {
    try {
      const apiResult = await fetchViaApi(accessToken)
      if (apiResult) return apiResult
    } catch {
      // fall through
    }
  }

  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle()

  if (error || !profile) {
    throw new Error('No user profile — contact your administrator')
  }

  let overrides: Array<{ permission_key: string; granted: boolean }> = []
  try {
    const { data } = await supabase
      .from('user_permission_overrides')
      .select('permission_key, granted')
      .eq('user_id', user.id)
    overrides = data || []
  } catch {
    overrides = []
  }

  const role = normalizeRole(profile.role)
  const perms = resolveEffectivePermissions(role, overrides, profile.is_active !== false)
  if (profile.is_super_admin) {
    ;['platform.tenants', 'users.view', 'users.manage', 'audit.view'].forEach((k) => perms.add(k))
  }

  let modules: string[] = []
  let tenant: TenantInfo | null = null
  if (profile.tenant_id) {
    const { data: tm } = await supabase
      .from('tenant_modules')
      .select('module_key')
      .eq('tenant_id', profile.tenant_id)
      .eq('enabled', true)
    modules = (tm || []).map((r: any) => r.module_key)
    const { data: t } = await supabase
      .from('tenants')
      .select('id, name, slug, status')
      .eq('id', profile.tenant_id)
      .maybeSingle()
    tenant = t as TenantInfo | null
  } else if (profile.is_super_admin) {
    // Platform-only: no tenant field-app modules
    modules = []
  }

  return {
    profile: { ...profile, role } as UserProfile,
    permissions: perms,
    modules,
    tenant,
    isSuperAdmin: !!profile.is_super_admin,
    canCreateSuperAdmins: !!(profile.is_super_admin && profile.can_create_super_admins),
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [permissions, setPermissions] = useState<Set<string>>(new Set())
  const [modules, setModules] = useState<string[]>([])
  const [tenant, setTenant] = useState<TenantInfo | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [canCreateSuperAdmins, setCanCreateSuperAdmins] = useState(false)
  const [loading, setLoading] = useState(true)
  const [permissionLoading, setPermissionLoading] = useState(false)

  const clearAuthState = useCallback(() => {
    setProfile(null)
    setPermissions(new Set())
    setModules([])
    setTenant(null)
    setIsSuperAdmin(false)
    setCanCreateSuperAdmins(false)
  }, [])

  const loadPermissions = useCallback(async (authUser: User | null, accessToken?: string) => {
    if (!authUser?.id) {
      clearPermissionCache()
      clearAuthState()
      return
    }
    setPermissionLoading(true)
    // Restore cached perms immediately so a camera/PWA reload offline does not
    // flash Access Denied while the network request fails.
    const cachedEarly = readPermissionCache(authUser.id)
    if (cachedEarly) {
      applyCachedAuth(cachedEarly, {
        setProfile,
        setPermissions,
        setModules,
        setTenant,
        setIsSuperAdmin,
        setCanCreateSuperAdmins,
      })
    }
    try {
      const result = await fetchProfileAndPermissions(authUser, accessToken)
      setProfile(result.profile)
      setPermissions(result.permissions)
      setModules(result.modules)
      setTenant(result.tenant)
      setIsSuperAdmin(result.isSuperAdmin)
      setCanCreateSuperAdmins(!!result.canCreateSuperAdmins)
      writePermissionCache({
        userId: authUser.id,
        profile: result.profile,
        permissions: Array.from(result.permissions),
        modules: result.modules,
        tenant: result.tenant,
        isSuperAdmin: result.isSuperAdmin,
        canCreateSuperAdmins: !!result.canCreateSuperAdmins,
      })
    } catch (e) {
      console.error('[auth]', e)
      // Offline / transient failure: keep cache (already applied) or prior in-memory state.
      // Never wipe perms here — empty Set sends field workers to Access Denied.
      if (!cachedEarly) {
        const cached = readPermissionCache(authUser.id)
        if (cached) {
          applyCachedAuth(cached, {
            setProfile,
            setPermissions,
            setModules,
            setTenant,
            setIsSuperAdmin,
            setCanCreateSuperAdmins,
          })
        }
      }
    } finally {
      setPermissionLoading(false)
    }
  }, [clearAuthState])

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadPermissions(user, session?.access_token)
  }, [user, session?.access_token, loadPermissions])

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }: { data: { session: Session | null } }) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
        if (session?.user) loadPermissions(session.user, session.access_token)
      })
      .catch(() => {
        setLoading(false)
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) loadPermissions(session.user, session.access_token)
      else {
        clearPermissionCache()
        clearAuthState()
      }
    })

    return () => subscription.unsubscribe()
  }, [loadPermissions, clearAuthState])

  const signIn = async (email: string, password: string) => {
    const result = await supabase.auth.signInWithPassword({ email, password })
    return result
  }

  const signUp = async (email: string, password: string, userData?: any) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: userData },
    })
    return { error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    clearPermissionCache()
    clearAuthState()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        permissions,
        modules,
        tenant,
        isSuperAdmin,
        canCreateSuperAdmins,
        loading,
        permissionLoading,
        signIn,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}